import { useTranslation } from "react-i18next";

import { EReputationMatchResult, EntityProfile } from "src/core/types/entity";
import { toSnakeCase } from "src/core/utils/text";

type InformationsPMSectionProps = {
  selectedMatch: EReputationMatchResult;
};

export const InformationsPMSection = ({ selectedMatch }: InformationsPMSectionProps) => {
  const { t } = useTranslation();
  const detail = selectedMatch?.detail as EntityProfile | undefined;

  const isNames = !!detail?.names?.length;
  const isRegistrationDates = !!detail?.registrationDates?.length;
  const isCessationDates = !!detail?.cessationDates?.length;
  const isCountries = !!detail?.countriesOfRegistration?.length;
  const isIndustries = !!detail?.industries?.length;
  const isIdentifiers = !!detail?.identifiers?.length;
  const isAddresses = !!detail?.addresses?.length;

  return (
    <div className="flex flex-row p-10 gap-8">
      {/* Colonne gauche */}
      <div className="flex flex-col w-1/2 space-y-[10px]">
        {/* Noms */}
        {isNames &&
          detail?.names?.map((group, index) => (
            <div key={`group-${index}`}>
              <div className="mb-1">
                {t(`modal.entityDetails.sections.informations.${toSnakeCase(group.type)}`, {
                  count: group.names.length,
                })}
                &nbsp;:
              </div>

              <div className="space-y-[5px] ml-4">
                {group.names.map((name, nameIdx) => {
                  return (
                    <div key={`name-${index}-${nameIdx}`}>
                      <ul className="list-disc list-inside">
                        <li className="font-light">{name.fullName}</li>
                      </ul>

                      {name.originalScripts && name.originalScripts.length > 0 && (
                        <div className="ml-6 mt-1 space-y-1 font-light">
                          {name.originalScripts.map((os, osIdx) => (
                            <div key={`script-${index}-${nameIdx}-${osIdx}`}>
                              {os.name} {os.langCode && <span>({os.langCode})</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

        {/* Industries */}
        {isIndustries && (
          <div>
            <div className="mb-1">
              {t("modal.entityDetails.sections.informations.industries", {
                count: detail?.industries?.length,
              })}
              &nbsp;:
            </div>
            <ul className="ml-4 list-disc list-inside font-light space-y-1">
              {detail?.industries?.map((industry, index) => (
                <li key={`industry-${index}`}>{industry.description}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Colonne droite */}
      <div className="flex flex-col w-1/2 border-l border-border pl-6 space-y-[10px]">
        {/* Dates d'enregistrement */}
        {isRegistrationDates && (
          <div>
            <div className="space-y-4">
              {detail?.registrationDates?.map((group, index) => (
                <div key={index}>
                  <div className="mb-1">
                    {t(`modal.entityDetails.sections.informations.${toSnakeCase(group.type)}`, {
                      count: group.dates.length,
                    })}
                    &nbsp;:
                  </div>
                  <ul className="list-disc list-inside space-y-1 ml-4 font-light">
                    {group.dates.map((d: string, i: number) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dates de cessation */}
        {isCessationDates && (
          <div>
            <div className="space-y-4">
              {detail?.cessationDates?.map((group, index) => (
                <div key={index}>
                  <div className="mb-1">
                    {t(`modal.entityDetails.sections.informations.${toSnakeCase(group.type)}`, {
                      count: group.dates.length,
                    })}
                    &nbsp;:
                  </div>
                  <ul className="list-disc list-inside space-y-1 ml-4 font-light">
                    {group.dates.map((d: string, i: number) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pays d’enregistrement */}
        {isCountries && (
          <>
            {detail?.countriesOfRegistration?.map((countryDetail, index) => (
              <div key={`country-detail-${index}`}>
                <div>
                  {t(
                    `modal.entityDetails.sections.informations.${toSnakeCase(countryDetail.type)}`,
                    {
                      count: countryDetail.countries.length,
                    }
                  )}
                  &nbsp;:
                </div>
                <ul className="ml-4 list-disc list-inside font-light space-y-1">
                  {countryDetail.countries.map((country, countryIndex) => (
                    <li key={`country-detail-${index}-country-${countryIndex}`}>{country}</li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        )}

        {/* Adresses */}
        {isAddresses && (
          <div>
            <div className="mb-1">
              {t("modal.entityDetails.sections.informations.addresses", {
                count: detail?.addresses?.length,
              })}
              &nbsp;:
            </div>
            <ul className="ml-4 list-disc list-inside font-light space-y-1">
              {detail?.addresses?.map((addr, index) => {
                const parts = [addr.addressLine, addr.city, addr.region, addr.country].filter(
                  Boolean
                );

                return <li key={`addr-${index}`}>{parts.join(", ")}</li>;
              })}
            </ul>
          </div>
        )}

        {/* Identifiants */}
        {isIdentifiers && (
          <div>
            <div className="mb-1">
              {t("modal.entityDetails.sections.informations.identifiers")}&nbsp;:
            </div>
            <ul className="ml-4 list-disc list-inside font-light space-y-1">
              {detail?.identifiers?.map((id, index) => (
                <li key={`identifier-${index}`}>
                  {id.type} : {id.value}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
